import { useLinkageDocuments } from './LinkageDocuments'
import { useLinkageAmount, useLinkageRepetition, useLinkageBusinessAPI } from '../LinkageCreationContext'
import { useContext, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import Lottie from 'lottie-react'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import CustomButton from '../../../../../ui/CustomButton'
import { Title } from '../../../../../ui'
import ResizableModal from '../../../../../ui/ResizableModal'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import { CentroCostosAPI } from '../../../../../../../api/centroCostos.api'
import GlobalContext from '../../../../../../../context/global-context'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import RepeatLinkageControl from './RepeatLinkageControl'
import { createRepeatedLinkages } from './repeatLinkage'

const CENTER_COLORS = [
  '#00C6B3',
  '#FF6B6B',
  '#4F46E5',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#A855F7',
  '#F97316',
  '#22C55E'
]

const buildCentroCostoPayload = rows => {
  return rows
    .filter(row => row.centroCostoId && row.porcentaje > 0)
    .map(row => ({
      idCentroCosto: Number(row.centroCostoId),
      porcentajeCentroCosto: row.porcentaje
    }))
}

const normalizeText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')

const toNumericId = value => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const getComparableCuentaId = value => {
  if (value === null || value === undefined || value === '') return null
  return toNumericId(value) ?? String(value)
}

const getCuentaOptionId = cuenta =>
  getComparableCuentaId(cuenta?.id_key) ??
  getComparableCuentaId(cuenta?.id) ??
  cuenta?.id_key ??
  cuenta?.id ??
  null

const getCuentaOptionLabel = cuenta => cuenta?.name || cuenta?.nombreCuenta || ''

const getCuentaDataId = data =>
  getComparableCuentaId(
    data?.cuentaContableId ??
    data?.idCuenta ??
    data?.id_key ??
    data?.id
  )

const getCuentaDataLabel = data =>
  data?.nombreCodigoDocumento ||
  data?.nombreComprobante ||
  data?.nombreCuenta ||
  ''

const getCuentaApiPayloadId = cuenta =>
  getComparableCuentaId(cuenta?.id_key) ?? getCuentaOptionId(cuenta)

const getCuentaOptionClassification = cuenta => {
  if (!cuenta) return ''

  const code =
    cuenta?.codigo ??
    cuenta?.codigoCuenta ??
    cuenta?.code ??
    cuenta?.codeCuenta ??
    cuenta?.codigoFinal ??
    ''
  const label = getCuentaOptionLabel(cuenta)

  if (code && label) return `${code} ${label}`
  return code || label || ''
}

const getSelectedCuentaFromData = (data, options) => {
  if (!data || !Array.isArray(options) || options.length === 0) return null

  const selectedById = options.find(
    cuenta => getCuentaOptionId(cuenta) === getCuentaDataId(data)
  )
  if (selectedById) return selectedById

  const normalizedCuentaNames = [
    data?.nombreCodigoDocumento,
    data?.nombreComprobante,
    data?.nombreCuenta
  ]
    .map(normalizeText)
    .filter(Boolean)

  if (normalizedCuentaNames.length === 0) return null

  return options.find(cuenta => {
    const comparableTexts = [
      getCuentaOptionLabel(cuenta),
      getCuentaOptionClassification(cuenta)
    ]
      .map(normalizeText)
      .filter(Boolean)

    return normalizedCuentaNames.some(name => comparableTexts.includes(name))
  }) || null
}

const InputField = ({ label, placeholder, type, value, onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm font-sans
        ${disabled
          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
          : 'focus:border-indigo-500 focus:ring-indigo-500'}
      `}
    />
  </div>
)

const TextAreaField = ({ label, value, onChange, ...props }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <textarea
      className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
      value={value}
      onChange={onChange}
      maxLength={50}
      required
      {...props}
    />
    <p className='text-xs text-gray-500 mt-1'>
      {value.length}/50 caracteres
    </p>
  </div>
)

const TypeSelectField = ({
  value,
  onChange,
  options = [],
  disabled = false,
  loading = false,
  fallbackOptionLabel = ''
}) => (
  <div>
    <label
      htmlFor='tipo-vinculacion'
      className='block text-sm font-sans font-medium text-gray-700'
    >
      Cuenta Contable
    </label>
    <select
      id='tipo-vinculacion'
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm font-sans
        ${disabled || loading
          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
          : 'focus:border-indigo-500 focus:ring-indigo-500'}
      `}
      onChange={onChange}
      value={value ?? ''}
      disabled={disabled || loading}
    >
      <option value='' disabled hidden>
        {loading ? 'Cargando opciones' : 'Seleccione una opcion'}
      </option>
      {!options.some(option => getCuentaOptionId(option) === getComparableCuentaId(value)) &&
        value !== null &&
        value !== undefined &&
        value !== '' &&
        fallbackOptionLabel && (
          <option value={value}>
            {fallbackOptionLabel}
          </option>
      )}
      {options.map(option => {
        const optionId = getCuentaOptionId(option)
        if (optionId === null || optionId === undefined) return null

        return (
          <option key={optionId} value={optionId}>
            {getCuentaOptionLabel(option)}
          </option>
        )
      })}
    </select>
  </div>
)

const isForbiddenCentroName = name =>
  name?.trim().toLowerCase() === 'estado de resultados consolidado'.toLowerCase()

const GenericoCargo = ({
  open,
  setOpen,
  type = 'egreso',
  setSwitchDate,
  idLibro, idOrigin,
  folio,
  action,
  modalTitle = 'Vinculaciones de Cargo',
  vinculacionApiType = 'gastos',
  createModel = 'vinculo-generico-compra'
}) => {
  const { ui, currentBusiness } = useContext(GlobalContext)
  const businessAPI = useLinkageBusinessAPI()
  const organizationAPI = new OrganizationAPI()
  const centroCostosAPI = new CentroCostosAPI()

  const [fecha, setFecha] = useState('')
  const [repeatLinkage, setRepeatLinkage] = useLinkageRepetition()
  const [repeatCount, setRepeatCount] = useState(1)
  const [vencimiento, setVencimiento] = useState('')
  const [glosa, setGlosa] = useState('')
  const [monto, setMonto, bankAmountLocked] = useLinkageAmount('')
  const [rut, setRut] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [tipoVinculacionId, setTipoVinculacionId] = useState('')
  const [data, setData] = useState({})
  const attachment = useLinkageDocuments({ open, action, idLibro, idOrigin, businessId: currentBusiness?.id, businessAPI })
  const [loading, setLoading] = useState(false)
  const [loadingVinculaciones, setLoadingVinculaciones] = useState(false)
  const [centroCostoError, setCentroCostoError] = useState('')
  const [initialCentroCostoPayload, setInitialCentroCostoPayload] = useState(null)
  const [centroCostos, setCentroCostos] = useState([])
  const [vinculacionOptions, setVinculacionOptions] = useState([])
  const [razonesS, setRazonesS] = useState({})
  const [centroCostoTemporalidad, setCentroCostoTemporalidad] = useState(0)
  const [initialCentroTemporalidad] = useState(0)

  const buildInitialRows = (count = 2) =>
    Array.from({ length: count }).map((_, i) => ({
      centroCostoId: '',
      porcentaje: 0,
      color: CENTER_COLORS[i % CENTER_COLORS.length]
    }))

  const [centrosCostoRows, setCentrosCostoRows] = useState(buildInitialRows(2))

  const selectedVinculacion = vinculacionOptions.find(
    option => getCuentaOptionId(option) === getComparableCuentaId(tipoVinculacionId)
  ) || null
  const selectedVinculacionLabel = getCuentaOptionLabel(selectedVinculacion) || getCuentaDataLabel(data) || ''
  const selectedVinculacionPayloadId =
    getCuentaApiPayloadId(selectedVinculacion) ?? getCuentaDataId(data)
  const selectedVinculacionClassification =
    getCuentaOptionClassification(selectedVinculacion) ||
    (data.codigoCuenta || data.nombreCuenta
      ? `${data.codigoCuenta || ''} ${data.nombreCuenta || ''}`.trim()
      : '')
  const isModifyOnlyCentroCosto = action === 'modificar'

  const getAvailableCentroCostosCount = () => {
    if (!Array.isArray(centroCostos)) return 0
    return centroCostos.filter(cc => !isForbiddenCentroName(cc.name)).length
  }

  const handleClose = () => setOpen(false)

  const functionGetData = async () => {
    try {
      setLoading(true)
      const response = await organizationAPI.getDataLibroContable(
        currentBusiness?.id,
        idLibro,
        type.trim()
      )
      setData(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const getRazones = async () => {
    try {
      const response = await organizationAPI.getRazonSocial(currentBusiness?.id)
      setRazonesS(response.data)
    } catch (error) {
      console.error(error)
    }
  }

  const getCentroCostos = async () => {
    try {
      if (!currentBusiness?.id) return
      const response = await centroCostosAPI.getCentroCostos(currentBusiness.id)
      setCentroCostos(response.data)
    } catch (error) {
      console.error('Error al cargar los centros de costo:', error)
      throw error
    }
  }

  const getVinculaciones = async () => {
    try {
      if (!currentBusiness?.id) {
        setVinculacionOptions([])
        return
      }

      setLoadingVinculaciones(true)
      const response = await organizationAPI.getCuentasContables({
        businessId: currentBusiness.id,
        type: vinculacionApiType
      })
      setVinculacionOptions(Array.isArray(response?.data) ? response.data : [])
    } catch (error) {
      console.error('Error al cargar vinculaciones:', error)
      setVinculacionOptions([])
    } finally {
      setLoadingVinculaciones(false)
    }
  }

  useEffect(() => {
    if (open && idLibro) {
      setTipoVinculacionId('')
      functionGetData()
      getRazones()
    }
  }, [open, idLibro])

  useEffect(() => {
    getCentroCostos()
    getVinculaciones()
  }, [currentBusiness?.id, vinculacionApiType])

  useEffect(() => {
    if (!data) return

    if (data.fecha) {
      const parsedDate = new Date(data.fecha)
      setFecha(parsedDate.toISOString().split('T')[0])
    }

    if (data.fechaVencimiento) {
      const parsedDate = new Date(data.fechaVencimiento)
      setVencimiento(parsedDate.toISOString().split('T')[0])
    }

    if (data.glosa) setGlosa(data.glosa)
    if (data.montoTotal) setMonto(data.montoTotal.toString())
    if (data.razonSocial) setRazonSocial(data.razonSocial)
    if (data.rut) setRut(data.rut)

    if (Array.isArray(data.centro) && data.centro.length > 0) {
      const rows = data.centro.map((c, index) => ({
        centroCostoId: c.centroCostoId,
        porcentaje: c.porcentajeCentroCosto,
        color: CENTER_COLORS[index % CENTER_COLORS.length]
      }))

      setCentrosCostoRows(rows)
      setInitialCentroCostoPayload(buildCentroCostoPayload(rows))
    } else {
      const initialRows = buildInitialRows(2)
      setCentrosCostoRows(initialRows)
      setInitialCentroCostoPayload(buildCentroCostoPayload(initialRows))
    }
  }, [data])

  useEffect(() => {
    if (!data) return

    const dataCuentaId = getCuentaDataId(data)
    if (dataCuentaId !== null && dataCuentaId !== undefined) {
      setTipoVinculacionId(dataCuentaId)
    }
  }, [data])

  useEffect(() => {
    const selectedCuenta = getSelectedCuentaFromData(data, vinculacionOptions)
    if (selectedCuenta) {
      setTipoVinculacionId(getCuentaOptionId(selectedCuenta))
    }
  }, [data, vinculacionOptions])

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    setFecha(today)
    setVencimiento(today)
  }, [])

  const validateCommonForm = () => {
    if (!selectedVinculacionPayloadId) {
      alert('Debes seleccionar una vinculacion')
      return false
    }

    if (!fecha || !vencimiento || !monto || !rut || !razonSocial || !glosa) {
      alert('Todos los campos son obligatorios excepto folio')
      return false
    }

    return true
  }

  const handleDatos = async () => {
    if (!validateCommonForm()) return

    const payload = {
      fecha,
      vencimiento,
      transaccionId: null,
      razonSocial: razonSocial || null,
      rut,
      rutId: null,
      nombreComprobante: selectedVinculacionLabel || null,
      nombreComprobanteId: null,
      glosa: glosa || null,
      respaldoId: null,
      monto: Number(monto),
      cuentaContableId: selectedVinculacionPayloadId
    }

    try {
      setLoading(true)
      const response = await createRepeatedLinkages({
        payload,
        repetitions: repeatLinkage ? repeatCount : 1,
        createLinkage: repeatedPayload => businessAPI.postPagoArriendo(
          currentBusiness.id,
          repeatedPayload,
          createModel
        )
      })
      await attachment.upload(response[0]?.data?.id)
      console.info(response)
      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error('Error al agregar comprobante:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!isModifyOnlyCentroCosto && !validateCommonForm()) return

    ui.setLoader({ visible: true })
    setCentroCostoError('')

    const centroCostoPayload = buildCentroCostoPayload(centrosCostoRows)
    const temporalidad = Number(centroCostoTemporalidad ?? 0) || 0

    const hasCentroCostoChanges = initialCentroCostoPayload
      ? JSON.stringify(centroCostoPayload) !== JSON.stringify(initialCentroCostoPayload) ||
        temporalidad !== initialCentroTemporalidad
      : centroCostoPayload.length > 0

    if (hasCentroCostoChanges && centroCostoPayload.length > 0) {
      const totalPorcentaje = centroCostoPayload.reduce(
        (acc, item) => acc + (item.porcentajeCentroCosto || 0),
        0
      )

      if (totalPorcentaje !== 100) {
        setCentroCostoError('La suma de porcentajes debe dar 100%')
        ui.setLoader({ visible: false })
        return
      }

      const ids = centroCostoPayload.map(i => i.idCentroCosto)
      if (new Set(ids).size !== ids.length) {
        setCentroCostoError('No puedes repetir centros de costo')
        ui.setLoader({ visible: false })
        return
      }
    }

    try {
      const promises = []

      if (!isModifyOnlyCentroCosto) {
        const payload = {
          fecha,
          vencimiento,
          glosa,
          monto: Number(monto),
          razonSocial,
          rut,
          nombreComprobante: selectedVinculacionLabel || null,
          cuentaContableId: selectedVinculacionPayloadId
        }

        promises.push(
          organizationAPI.updateVinculacion(currentBusiness?.id, idLibro, payload, createModel)
        )

        const razonSeleccionada = razonesS?.find(r => r.rut === rut)

        if (razonSeleccionada?.code) {
          const idRazonSocial = razonSeleccionada.code.split('-')[0]

          promises.push(
            organizationAPI.patchRazonSocial(
              currentBusiness.id,
              idRazonSocial,
              { razonSocial }
            )
          )
        }
      }

      if (hasCentroCostoChanges) {
        promises.push(
          centroCostosAPI.createLibroCentroCosto(
            currentBusiness.id,
            idLibro,
            centroCostoPayload,
            temporalidad
          )
        )
      }

      await Promise.all(promises)

      setSwitchDate(true)
      functionGetData()
      handleClose()
    } catch (error) {
      console.error('ERROR handleSubmit:', error)
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  const formatMonto = value => {
    if (!value) return ''
    const numericValue = value.replace(/\D/g, '')
    if (!numericValue) return ''
    return new Intl.NumberFormat('es-CL').format(numericValue)
  }

  const formatRut = input => {
    let cleaned = input.replace(/[^0-9kK]/g, '').toUpperCase()
    if (cleaned.length > 9) cleaned = cleaned.slice(0, 9)
    const body = cleaned.slice(0, -1)
    const dv = cleaned.slice(-1)
    let formattedBody = ''
    for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
      formattedBody = body[i] + formattedBody
      if (j % 3 === 0 && i !== 0) {
        formattedBody = '.' + formattedBody
      }
    }
    if (body.length === 0) return dv
    return `${formattedBody}-${dv}`
  }

  const getCentroCostoOptionsForRow = () => {
    if (!Array.isArray(centroCostos)) return []

    return centroCostos
      .filter(cc => !isForbiddenCentroName(cc.name))
      .map(cc => ({
        id: cc.id,
        nombreCuenta: cc.name
      }))
  }

  const CircleProgress = ({ rows }) => {
    const total = Math.min(
      100,
      rows.reduce((acc, r) => acc + (Number(r.porcentaje) || 0), 0)
    )

    let offset = 0
    const stops = []

    rows.forEach(row => {
      const pct = Math.max(0, Math.min(100 - offset, Number(row.porcentaje) || 0))
      if (pct <= 0) return
      const color = row.color || '#00C6B3'
      stops.push(`${color} ${offset}% ${offset + pct}%`)
      offset += pct
    })

    if (offset < 100) {
      stops.push(`#E5E7EB ${offset}% 100%`)
    }

    const background = `conic-gradient(${stops.join(', ')})`

    return (
      <div className='flex flex-col items-center mt-3'>
        <div
          className='w-20 h-20 rounded-full flex items-center justify-center'
          style={{ background }}
        >
          <span className='text-xs font-semibold'>{total}%</span>
        </div>
        <span className='mt-1 text-[11px] text-gray-500 text-center font-sans'>
          Distribucion % centros de costo
        </span>
      </div>
    )
  }

  const handleCentroCostoRowChange = (index, field, value) => {
    setCentrosCostoRows(prev => {
      const clone = [...prev]

      if (field === 'porcentaje') {
        const parsed = value === '' ? '' : Math.max(0, Math.min(100, Number(value) || 0))

        clone[index] = {
          ...clone[index],
          porcentaje: parsed
        }
      } else {
        clone[index] = {
          ...clone[index],
          [field]: value
        }
      }

      return clone
    })
    setCentroCostoError('')
  }

  const addCentroCostoRow = () => {
    setCentrosCostoRows(prev => {
      const maxRows = getAvailableCentroCostosCount()
      if (maxRows > 0 && prev.length >= maxRows) {
        return prev
      }

      const nextIndex = prev.length
      const newRow = {
        centroCostoId: '',
        porcentaje: 0,
        color: CENTER_COLORS[nextIndex % CENTER_COLORS.length]
      }

      return [...prev, newRow]
    })
    setCentroCostoError('')
  }

  const removeCentroCostoRow = index => {
    setCentrosCostoRows(prev => prev.filter((_, i) => i !== index))
    setCentroCostoError('')
  }

  const clearCentroCostoRows = () => {
    setCentrosCostoRows([])
    setCentroCostoError('')
  }

  const CustomSelect = ({ label, name, options, value, onChange, disabled }) => (
    <div className='relative flex flex-col items-start'>
      {label && (
        <label
          htmlFor={name}
          className='absolute top-[-15px] left-4 bg-transparent text-xs font-sans font-medium text-gray-500'
        >
          {label}
        </label>
      )}
      <select
        className='h-9 w-full appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs font-sans'
        name={name}
        onChange={onChange}
        value={value}
        disabled={disabled}
      >
        <option value='' disabled hidden>
          Seleccione una opcion
        </option>
        {options.map(option => (
          <option key={option.id} value={option.id}>
            {option.nombreCuenta}
          </option>
        ))}
      </select>
    </div>
  )

  const maxCentroRows = getAvailableCentroCostosCount()

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton title='CANCELAR' color='primary' className='rounded-full' onClick={handleClose} />
          <CustomButton
            title={
              action === 'replicar'
                ? 'REPLICAR VINCULACION'
                : action === 'modificar'
                  ? 'GRABAR CENTRO DE COSTO'
                  : 'GRABAR VINCULACION'
            }
            color='accent2'
            className='rounded-full'
            disabled={loading || attachment.busy}
            onClick={action === 'modificar' ? handleSubmit : handleDatos}
          />
        </>
      }
    >
      <>
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
            <Lottie
              animationData={LoaderMain}
              loop
              autoplay
              style={{ width: 100, height: 100 }}
            />
          </Box>
        ) : (
          <>
            <div>
              <div className='flex justify-between items-center mb-4'>
                <div>
                  <Title className='text-2xl font-sans'>{modalTitle}</Title>
                </div>
              </div>
              <div className="flex w-full items-start h-full p-3 bg-white rounded-lg shadow-md'">
                <div className='flex flex-col gap-6 w-full'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
                    <InputField label='Folio' value={folio} type='text' disabled />
                    <div>
                      <label className='block text-sm font-sans font-medium text-gray-700'>Fecha</label>
                      <input
                        type='date'
                        value={fecha}
                        onChange={e => setFecha(e.target.value)}
                        disabled={isModifyOnlyCentroCosto}
                        className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-sans font-medium text-gray-700'>Fecha Vencimiento</label>
                      <input
                        type='date'
                        value={vencimiento}
                        onChange={e => setVencimiento(e.target.value)}
                        disabled={isModifyOnlyCentroCosto}
                        className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
                      />
                    </div>
                    <InputField
                      label='Contraparte'
                      placeholder='Nombre'
                      type='text'
                      value={razonSocial}
                      onChange={e => setRazonSocial(e.target.value)}
                      disabled={isModifyOnlyCentroCosto}
                    />
                    <InputField
                      label='RUT/TAX ID'
                      placeholder='11.111.111-1'
                      type='text'
                      value={formatRut(rut)}
                      onChange={e => setRut(formatRut(e.target.value))}
                      disabled={isModifyOnlyCentroCosto}
                    />
                    <TypeSelectField
                      value={tipoVinculacionId}
                      onChange={e => setTipoVinculacionId(getComparableCuentaId(e.target.value))}
                      options={vinculacionOptions}
                      disabled={data.validation === -5 || isModifyOnlyCentroCosto}
                      loading={loadingVinculaciones}
                      fallbackOptionLabel={selectedVinculacionLabel}
                    />
                    <InputField
                      label='Clasificacion'
                      placeholder='Se asigna automaticamente'
                      type='text'
                      value={selectedVinculacionClassification}
                      disabled
                    />
                    <InputField
                      label='Monto'
                      placeholder='$'
                      type='text'
                      value={formatMonto(monto)}
                      onChange={e => setMonto(e.target.value.replace(/[^0-9]/g, ''))}
                      disabled={bankAmountLocked || (isModifyOnlyCentroCosto)}
                    />
                    <div className='col-span-full grid min-w-0 grid-cols-1 items-start gap-4 md:grid-cols-2'>
                  <div className='min-w-0 space-y-4'>
                    <TextAreaField
                      label='Detalle'
                      placeholder='Describe en detalle la naturaleza, origen o lo que mejor describa la vincululacion que vamos a crear.'
                      value={glosa}
                      onChange={e => setGlosa(e.target.value)}
                      disabled={isModifyOnlyCentroCosto}
                    />
                    {action !== 'modificar' && (
                      <RepeatLinkageControl checked={repeatLinkage} count={repeatCount}
                        onCheckedChange={setRepeatLinkage} onCountChange={setRepeatCount} />
                    )}
                  </div>
                  {attachment.panel}
                </div>
              </div>
                  {(action === 'modificar' || action === 'replicar') && (
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 w-full'>
                      <div className='md:col-span-1'>
                        <CircleProgress rows={centrosCostoRows} />
                      </div>
                      <div className='md:col-span-2'>
                        <div className='grid grid-cols-1 gap-4'>
                          <div>
                            <div className='relative flex flex-col'>
                              <label className='absolute -top-3 left-4 z-10 bg-white px-1 text-xs font-sans font-medium text-gray-500'>
                                Temporalidad centro costos
                              </label>
                              <select
                                className='h-9 w-full border border-gray-300 rounded-full px-4 text-xs font-sans'
                                value={centroCostoTemporalidad}
                                onChange={e => setCentroCostoTemporalidad(Number(e.target.value))}
                                disabled={data.validation === -5}
                              >
                                <option value={0}>Solo este movimiento</option>
                                <option value={1}>Este registro y todos los anteriores</option>
                                <option value={2}>Este registro y todos los posteriores</option>
                                <option value={3}>Aplicar a todos los registros</option>
                              </select>
                            </div>
                          </div>

                          <div className='flex flex-col gap-3'>
                            {centrosCostoRows.map((row, index) => (
                              <div
                                key={index}
                                className='grid grid-cols-[1fr_160px] gap-3 items-end'
                              >
                                <CustomSelect
                                  label='Centro de costo'
                                  name={`centroCosto-${index}`}
                                  options={getCentroCostoOptionsForRow()}
                                  value={row.centroCostoId}
                                  onChange={e =>
                                    handleCentroCostoRowChange(index, 'centroCostoId', e.target.value)
                                  }
                                  disabled={data.validation === -5}
                                />

                                <div>
                                  <label className='text-xs text-gray-500 font-sans'>Porcentaje</label>
                                  <div className='flex items-center gap-2'>
                                    <input
                                      type='number'
                                      min={0}
                                      max={100}
                                      className='h-9 w-full border border-gray-300 rounded-full px-3 text-xs'
                                      value={row.porcentaje === 0 ? '' : row.porcentaje}
                                      onChange={e =>
                                        handleCentroCostoRowChange(index, 'porcentaje', e.target.value)
                                      }
                                      disabled={data.validation === -5}
                                    />

                                    {centrosCostoRows.length > 0 && (
                                      <button
                                        type='button'
                                        onClick={() => removeCentroCostoRow(index)}
                                        className='text-red-500 hover:text-red-700 font-sans'
                                        title='Eliminar centro de costo'
                                      >
                                        <ClearOutlinedIcon fontSize='small' />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className='flex w-full items-center justify-between font-sans'>
                              <button
                                type='button'
                                className={`text-xs ${
                                  maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-accent3'
                                }`}
                                onClick={addCentroCostoRow}
                                disabled={
                                  data.validation === -5 ||
                                  (maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows)
                                }
                              >
                                + Agregar centro de costo
                              </button>
                              <button
                                type='button'
                                className={`text-xs ${
                                  data.validation === -5 || centrosCostoRows.length === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-red-500 hover:text-red-600'
                                }`}
                                onClick={clearCentroCostoRows}
                                disabled={data.validation === -5 || centrosCostoRows.length === 0}
                              >
                                - Quitar asignacion
                              </button>
                            </div>
                          </div>

                          {centroCostoError && (
                            <p className='text-xs text-red-500 font-sans'>{centroCostoError}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </ResizableModal>
  )
}

export default GenericoCargo
